import { Component, EventEmitter, forwardRef, Input, OnChanges, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * A checkbox and its label, with ngModel or a form control:
 *
 *     <gains-checkbox formControlName="forceRescan" inputId="forceRescan">Force Rescan</gains-checkbox>
 *
 * Disabled by its form (`control.disable()`, `[disabled]` with ngModel) or by `disabledState`: either one disables it.
 */
@Component({
  selector: 'gains-checkbox',
  standalone: false,
  template: `<!-- eslint-disable-next-line @angular-eslint/template/label-has-associated-control -- ng-zorro's label holds its own input -->
    <label nz-checkbox [nzId]="inputId ?? null" [ngModel]="checked" (ngModelChange)="onToggle($event)" [nzDisabled]="disabled" [nzIndeterminate]="indeterminate"
      ><ng-content></ng-content
    ></label>`,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => GainsCheckboxComponent), multi: true }]
})
export class GainsCheckboxComponent implements ControlValueAccessor, OnChanges {
  /** The id of its input, for a `<label for>` elsewhere. */
  @Input() inputId?: string;
  /** Disabled apart from its form, e.g. while saving. */
  @Input() disabledState = false;
  /** Neither checked nor unchecked: some of the items it stands for are checked. */
  @Input() indeterminate = false;
  /** The new state, as the user checked it. */
  @Output() checkedChange = new EventEmitter<boolean>();

  checked = false;
  formDisabled = false;
  /** Disabled by its form or by `disabledState`. */
  disabled = false;
  private onChange: (checked: boolean) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  ngOnChanges(): void {
    this.disabled = this.disabledState || this.formDisabled;
  }

  writeValue(value: unknown): void {
    this.checked = !!value;
  }

  registerOnChange(fn: (checked: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formDisabled = isDisabled;
    this.disabled = this.disabledState || this.formDisabled;
  }

  onToggle(checked: boolean): void {
    this.checked = checked;
    this.onChange(checked);
    this.onTouched();
    this.checkedChange.emit(checked);
  }
}
